import React from 'react';
import { Layout, Menu, Typography } from 'antd';
import {
  HeartOutlined, CloudServerOutlined, FileTextOutlined, AlertOutlined, KeyOutlined,
  TeamOutlined, ApiOutlined, HistoryOutlined, SettingOutlined, MedicineBoxOutlined,
  BookOutlined, LineChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { HEADER_HEIGHT, SIDER_COLLAPSED_WIDTH, SIDER_WIDTH } from '../theme';

const { Sider } = Layout;
const { Text } = Typography;

/** Route prefix -> menu key. First match wins, so order matters. */
const KEY_BY_PREFIX: [string, string][] = [
  ['/servers', 'servers'],
  ['/reports', 'reports'],
  ['/tickets', 'tickets'],
  ['/api-keys', 'api-keys'],
  ['/users', 'users'],
  ['/webhooks', 'webhooks'],
  ['/check-logs', 'check-logs'],
  ['/worker-analytics', 'worker-analytics'],
  ['/settings', 'settings'],
  ['/diagnostics', 'diagnostics'],
  ['/integration-docs', 'integration-docs'],
];

const selectedKeyFor = (pathname: string): string => {
  if (pathname === '/' || pathname.includes('/health')) return 'dashboard';
  const hit = KEY_BY_PREFIX.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? hit[1] : 'dashboard';
};

interface AppSiderProps {
  collapsed: boolean;
  isAdmin: boolean;
}

/** Navigation rail. Collapsed it is an icon strip with tooltips — no label is lost. */
const AppSider: React.FC<AppSiderProps> = ({ collapsed, isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Sider
      width={SIDER_WIDTH}
      collapsedWidth={SIDER_COLLAPSED_WIDTH}
      collapsed={collapsed}
      trigger={null}
      collapsible
    >
      <div
        style={{
          height: HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 0 : '0 12px',
          overflow: 'hidden',
        }}
      >
        <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
          {collapsed ? 'PND' : 'Proxy Network Diag'}
        </Text>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKeyFor(location.pathname)]}
        onClick={(e) => navigate(e.key === 'dashboard' ? '/' : `/${e.key}`)}
        items={[
          { key: 'dashboard', icon: <HeartOutlined />, label: 'Health Monitor' },
          { key: 'servers', icon: <CloudServerOutlined />, label: 'Servers' },
          { key: 'reports', icon: <FileTextOutlined />, label: 'Reports' },
          { key: 'tickets', icon: <AlertOutlined />, label: 'Tickets' },
          // Three groups split by rules — watch the fleet / investigate it /
          // configure the panel. Collapsed, the rail is bare icons, and the
          // breaks are what make it scannable. Labels stay set so antd shows
          // each one as a hover tooltip in the collapsed rail.
          ...(isAdmin ? [
            { type: 'divider' as const, style: { margin: '4px 8px' } },
            { key: 'check-logs', icon: <HistoryOutlined />, label: 'Check Logs' },
            { key: 'worker-analytics', icon: <LineChartOutlined />, label: 'Worker Analytics' },
            { key: 'diagnostics', icon: <MedicineBoxOutlined />, label: 'Diagnostics' },
            { type: 'divider' as const, style: { margin: '4px 8px' } },
            { key: 'users', icon: <TeamOutlined />, label: 'Users' },
            { key: 'api-keys', icon: <KeyOutlined />, label: 'API Keys' },
            { key: 'integration-docs', icon: <BookOutlined />, label: 'Integration API' },
            { key: 'webhooks', icon: <ApiOutlined />, label: 'Webhooks' },
            { key: 'settings', icon: <SettingOutlined />, label: 'Settings' },
          ] : []),
        ]}
      />
    </Sider>
  );
};

export default AppSider;
